import { supabase } from '../config/supabase';

export interface DigitalLiteracyQuestion {
  id: string;
  category: string;
  statement: string;
  example?: string;
  order_index: number;
  created_at: string;
}

export interface DigitalLiteracyAttempt {
  id: string;
  user_id: string;
  started_at: string;
  completed_at?: string;
  answers: Record<string, number>;
  category_scores: Record<string, number>;
  overall_score: number;
  created_at: string;
}

export const digitalLiteracyScanApi = {
  async getQuestions(): Promise<DigitalLiteracyQuestion[]> {
    const { data, error } = await supabase
      .from('digital_literacy_questions')
      .select('*')
      .order('order_index', { ascending: true });

    if (error) {
      throw new Error(`Failed to fetch digital literacy questions: ${error.message}`);
    }

    return data || [];
  },

  async createAttempt(userId: string): Promise<DigitalLiteracyAttempt> {
    const { data, error } = await supabase
      .from('digital_literacy_attempts')
      .insert({
        user_id: userId,
        answers: {},
        category_scores: {},
        overall_score: 0
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create digital literacy attempt: ${error.message}`);
    }

    return data;
  },

  async updateAttempt(attemptId: string, answers: Record<string, number>): Promise<DigitalLiteracyAttempt> {
    // Calculate category scores and overall score
    const questions = await this.getQuestions();
    const categoryScores: Record<string, number[]> = {};
    
    // Group answers by category
    questions.forEach(question => {
      if (answers[question.id]) {
        if (!categoryScores[question.category]) {
          categoryScores[question.category] = [];
        }
        categoryScores[question.category].push(answers[question.id]);
      }
    });

    // Calculate average scores per category
    const categoryAverages: Record<string, number> = {};
    Object.entries(categoryScores).forEach(([category, scores]) => {
      categoryAverages[category] = scores.reduce((sum, score) => sum + score, 0) / scores.length;
    });

    // Calculate overall score
    const allScores = Object.values(answers);
    const overallScore = allScores.length > 0 
      ? allScores.reduce((sum, score) => sum + score, 0) / allScores.length 
      : 0;

    const { data, error } = await supabase
      .from('digital_literacy_attempts')
      .update({
        answers,
        category_scores: categoryAverages,
        overall_score: overallScore,
        completed_at: new Date().toISOString()
      })
      .eq('id', attemptId)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update digital literacy attempt: ${error.message}`);
    }

    return data;
  },

  async getAttempt(attemptId: string): Promise<DigitalLiteracyAttempt> {
    const { data, error } = await supabase
      .from('digital_literacy_attempts')
      .select('*')
      .eq('id', attemptId)
      .single();

    if (error) {
      throw new Error(`Failed to fetch digital literacy attempt: ${error.message}`);
    }

    return data;
  }
};
